import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { quotationReducer } from 'features/quotation/slice';
import { quotationOnlyFlightsReducer } from 'features/quotation/sliceOnlyFlights';
import { reducer as authReducer } from 'features/auth/slice';
import { paymentReducer } from 'features/payment/slice';
import { initAuthInterceptors } from '../features/auth/services/AuthService';

export const rootReducer = combineReducers({
	quotation: quotationReducer,
	quotationOnlyFlights: quotationOnlyFlightsReducer,
	auth: authReducer,
	payment: paymentReducer,
});

const persistConfig = {
	key: 'root-v9',
	version: 9,
	storage,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
	reducer: persistedReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
			},
		}),
});

initAuthInterceptors(store);
export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export default store;

// ─── Dev helpers (solo disponibles fuera de producción) ──────────────────────
// Permiten simular distintos usuarios/roles/teams sin necesitar datos reales
// en el entorno de testing.

if (process.env.NODE_ENV !== 'production') {
	// Exponer el store para inspección manual
	(window as Record<string, unknown>)['__REDUX_STORE__'] = store;

	// Helper: simular un usuario con overrides parciales (id, role, etc.)
	// Parchea el estado de Redux Persist en localStorage y recarga la página.
	// Uso:
	//   __devSetUser({ id: 42, role: 5 })        → simular vendedor con id 42 (SELLER)
	//   __devSetUser({ id: 10, role: 1 })        → simular team lead con id 10 (ADMIN)
	//   __devSetUser({ id: 99, role: 10 })       → simular manager con id 99 (SUPERADMIN)
	(window as Record<string, unknown>)['__devSetUser'] = (overrides: Record<string, unknown>) => {
		const PERSIST_KEY = 'persist:root-v9';
		const raw = localStorage.getItem(PERSIST_KEY);
		if (!raw) {
			console.error('[dev] No se encontró el estado persistido. ¿Estás logueado?');
			return;
		}
		// Normalizar role: acepta string ('SUPERADMIN', 'ADMIN', 'SELLER') o número
		const ROLE_MAP: Record<string, number> = {
			SUPERADMIN: 10,
			SUPER_ADMIN: 10,
			ADMIN: 1,
			SELLER: 4,
			OPERATOR: 5,
			USER: 2,
			VIEW: 3,
		};
		const normalized = { ...overrides };
		if (typeof normalized.role === 'string') {
			const mapped = ROLE_MAP[normalized.role.toUpperCase()];
			if (mapped !== undefined) {
				normalized.role = mapped;
			} else {
				console.warn(`[dev] Rol desconocido: "${normalized.role}". Válidos: SUPERADMIN, ADMIN, SELLER, OPERATOR`);
			}
		}
		try {
			const root = JSON.parse(raw) as Record<string, string>;
			const auth = JSON.parse(root.auth) as Record<string, unknown>;
			const currentUser = auth.user as Record<string, unknown>;
			if (!currentUser) {
				console.error('[dev] No hay usuario en el estado. ¿Estás logueado?');
				return;
			}
			auth.user = { ...currentUser, ...normalized };
			root.auth = JSON.stringify(auth);
			localStorage.setItem(PERSIST_KEY, JSON.stringify(root));
			console.log('[dev] Usuario actualizado. Recargando…', auth.user);
			location.reload();
		} catch (e) {
			console.error('[dev] Error al parchear el estado:', e);
		}
	};

	// Helper: mostrar guía de uso rápido en consola
	(window as Record<string, unknown>)['__devHelp'] = () => {
		console.log(`
╔══════════════════════════════════════════════════════════╗
║              Dev Helpers — Sales Dashboard               ║
╠══════════════════════════════════════════════════════════╣
║ SIMULAR TEAM (sin recarga)                               ║
║   localStorage.setItem('__dev_team__', 'WTC UY')         ║
║   localStorage.removeItem('__dev_team__')  ← desactiva   ║
║   Teams válidos: WTC UY · PY · CENTRO UY · BO · CO       ║
║                                                          ║
║ SIMULAR ROL + ID DE USUARIO (con recarga)                ║
║   RolesEnum: SELLER=4 · ADMIN=1 · SUPERADMIN=10          ║
║   __devSetUser({ id: 57, role: 4 })   → vendedor         ║
║   __devSetUser({ id: 57, role: 1 })   → team lead        ║
║   __devSetUser({ id: 57, role: 10 })  → manager          ║
║                                                          ║
║ VER ESTADO ACTUAL                                        ║
║   __REDUX_STORE__.getState().auth.user                   ║
║                                                          ║
║ RESTAURAR USUARIO REAL (reemplazá con tus datos)         ║
║   localStorage.removeItem('__dev_team__')                ║
║   __devSetUser({ id: TU_ID, role: TU_ROL })              ║
╚══════════════════════════════════════════════════════════╝
    `);
	};

	console.info('[dev] Helpers disponibles: __devHelp() · __devSetUser() · __REDUX_STORE__ · localStorage.__dev_team__');
}
